import Keycloak, { KeycloakOnLoad } from "keycloak-js";

export class SrmSsoAuthenticationService {
    private static instance: SrmSsoAuthenticationService;
    private readonly _keycloakInstance: Keycloak;

    private static readonly sessionKeyToken = "sso_token";
    private static readonly sessionKeyRefreshToken = "sso_refresh_token";
    private static readonly sessionKeyUsername = "sso_username";

    public constructor() {
        this._keycloakInstance = new Keycloak({
            url: window.__CONFIG__.SRMSSO?.Url,
            realm: window.__CONFIG__.SRMSSO?.Realm,
            clientId: window.__CONFIG__.SRMSSO?.Resource,
        });
    }

    public static getInstance() {
        if (!SrmSsoAuthenticationService.instance) {
            SrmSsoAuthenticationService.instance = new SrmSsoAuthenticationService();
        }

        return SrmSsoAuthenticationService.instance;
    }

    public getKeycloak() {
        return this._keycloakInstance;
    }

    public async initialize(onSuccess: (userName: string, dropshipmentToken: string) => void,
                            onError: (authenticationUrl: string) => void) {
        const keyCloak = SrmSsoAuthenticationService.getInstance().getKeycloak();

        if (!keyCloak.didInitialize) {
            try {
                const data = await keyCloak.init(this.fetchInitializationSettings);

                if (data) {
                    sessionStorage.setItem(SrmSsoAuthenticationService.sessionKeyToken, keyCloak.token!);
                    sessionStorage.setItem(SrmSsoAuthenticationService.sessionKeyRefreshToken, keyCloak.refreshToken!);
                    sessionStorage.setItem(SrmSsoAuthenticationService.sessionKeyUsername, keyCloak.tokenParsed!.name);

                    const dropshipmentToken = await this.fetchDropshipmentToken(keyCloak.token!);
                    if (!this.hasSrmAgentRole(dropshipmentToken)) {
                        throw new Error(`User ${keyCloak.tokenParsed!.name} does not have the SrmAgent role`);
                    }

                    onSuccess(keyCloak.tokenParsed!.name, dropshipmentToken);
                } else {
                    throw new Error(`Unable to get token ${keyCloak.token!}`);
                }
            } catch (error) {
                onError(await keyCloak.createLoginUrl());
            }
        }
    }

    public async logout(onSuccess: (authenticationUrl: string) => void) {
        const keyCloak = SrmSsoAuthenticationService.getInstance().getKeycloak();
        await keyCloak.logout();

        sessionStorage.setItem(SrmSsoAuthenticationService.sessionKeyToken, "");
        sessionStorage.setItem(SrmSsoAuthenticationService.sessionKeyRefreshToken, "");
        sessionStorage.setItem(SrmSsoAuthenticationService.sessionKeyUsername, "");

        onSuccess(await keyCloak.createLoginUrl());
    }

    private get fetchInitializationSettings() {
        if (sessionStorage.getItem(SrmSsoAuthenticationService.sessionKeyToken)
            && sessionStorage.getItem(SrmSsoAuthenticationService.sessionKeyRefreshToken)
            && sessionStorage.getItem(SrmSsoAuthenticationService.sessionKeyUsername)
        ) {
            return {
                onLoad: undefined,
                token: sessionStorage.getItem(SrmSsoAuthenticationService.sessionKeyToken)!,
                refreshToken: sessionStorage.getItem(SrmSsoAuthenticationService.sessionKeyRefreshToken)!,
                checkLoginIframe: false,
            };
        }

        return {
            onLoad: "login-required" as KeycloakOnLoad,
            checkLoginIframe: false,
        };
    }

    private fetchDropshipmentToken = async (token: string) => {
        return ((await (await fetch("/api/v3/auth/sso/token", {
            method: "POST",
            headers: {
                Authorization: `Bearer  ${token}`,
            },
        })).json()) as ({ access_token: string})).access_token;
    }

    private hasSrmAgentRole(token: string) {
        const roles = this.getRolesFromToken(token);
        return roles.includes("SrmAgent");
    }

    private getRolesFromToken(token: string) {
        const splitToken = token.split(".");
        if (splitToken.length !== 3) {
            throw new Error(`Invalid token : "${token}"`);
        }

        return JSON.parse(atob(splitToken[1]))["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] as string[];
    }
}
